import { useState, useCallback } from 'react';
import { ICartGroup } from '@/api/types/cart';
import logger from '@/utils/client-logger';

const CART_SNAPSHOT_KEY = 'zl-cartSnapshot';

export const useCartSnapshot = () => {
  // 会话级别的购物车快照，用于准确识别新添加的商品
  const [cartSnapshot, setCartSnapshot] = useState<Set<number>>(new Set());

  // 初始化购物车快照
  const initializeCartSnapshot = useCallback((cartList: ICartGroup[]) => {
    const currentCartIds = new Set<number>();
    cartList.forEach((group) => {
      group.carts?.forEach((cart) => {
        if (cart.cart_id) {
          currentCartIds.add(cart.cart_id);
        }
      });
    });

    // 从sessionStorage读取之前的快照
    let previousSnapshot = new Set<number>();
    try {
      if (typeof window !== 'undefined') {
        const saved = sessionStorage.getItem(CART_SNAPSHOT_KEY);
        if (saved) {
          const snapshotArray = JSON.parse(saved);
          // 添加类型检查，确保是数组
          if (Array.isArray(snapshotArray)) {
            previousSnapshot = new Set(snapshotArray as number[]);
          }
        }
      }
    } catch (e) {
      logger.warn('Failed to load cart snapshot:', e);
    }

    // 如果是页面刷新（快照存在），使用之前的快照
    // 如果是新进入（快照不存在），使用当前商品作为快照
    const snapshotToUse = previousSnapshot.size > 0 ? previousSnapshot : currentCartIds;

    setCartSnapshot(snapshotToUse);

    // 保存到sessionStorage
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(CART_SNAPSHOT_KEY, JSON.stringify(Array.from(snapshotToUse)));
      }
    } catch (error) {
      logger.error('Failed to save cart snapshot:', error);
    }

    return snapshotToUse;
  }, []);

  // 添加商品到快照
  const addToCartSnapshot = useCallback((newCartIds: number[]) => {
    setCartSnapshot((prev) => {
      const newSnapshot = new Set([...Array.from(prev), ...newCartIds]);
      try {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(CART_SNAPSHOT_KEY, JSON.stringify(Array.from(newSnapshot)));
        }
      } catch (error) {
        logger.error('Failed to update cart snapshot:', error);
      }
      return newSnapshot;
    });
  }, []);

  return {
    cartSnapshot,
    initializeCartSnapshot,
    addToCartSnapshot,
  };
};
